// Events resource — the mutation path for changing task state, plus
// the case-scoped history query.
//
// State changes are framed as user actions ("Mark done", "Reopen"),
// not state assignments — the engine still receives an event with a
// target state, but the UI never exposes a state picker.

import {
  queryOptions,
  useMutation,
  useQueryClient,
  type QueryClient,
} from "@tanstack/react-query";

import { halFetch, type HalCollection } from "~/lib/hal";
import { CASES_LIST_KEY } from "~/lib/cases";
import {
  HANDOFF_TASK_TYPE,
  RESOLVE_CONTACT_TASK_TYPE,
  RETRY_HANDOFF_TASK_TYPE,
} from "~/lib/tasks";
import type { User } from "~/lib/users";
import type { TaskMeta } from "~/lib/workflows";

/**
 * Lifecycle states the engine accepts. `complete`, `todo`,
 * `cancelled`, `rejected`, `delayed`, and `in progress` are user-
 * facing transitions. Other states (un-cancel, error, "") are
 * internal — never sent from the UI, but parsed when reading event
 * history so the verb-mapping handles them gracefully.
 */
export type EventState =
  | "complete"
  | "todo"
  | "in progress"
  | "cancelled"
  | "rejected"
  | "delayed"
  | "un-cancel"
  | "error"
  | "";

/**
 * A single condition on an event. The Go side serializes Condition
 * with `task: *workflow.Task` — a structured object, not a bare
 * string. The label `taskLabelFromCondition` returns is a display form of
 * that object, and the history keys on it. A state change posts the row's
 * `task_id`, and the stored condition carries the row's identity.
 */
export interface EventCondition {
  task?: TaskMeta | null;
  state?: EventState;
}

/**
 * Derive the task's display label from a structured condition. It follows
 * `Task.String()` on the Go side but is shorter than the row's name: it
 * omits the target types.
 */
export function taskLabelFromCondition(
  task: TaskMeta | null | undefined,
): string {
  if (task == null) return "(unknown task)";
  const subj = task.subject;
  const ass = task.assignee;
  if (subj !== undefined) {
    if (ass !== undefined) return `${task.type} for ${subj.key} by ${ass.key}`;
    return `${task.type} for ${subj.key}`;
  }
  if (ass !== undefined) return `${task.type} by ${ass.key}`;
  return task.type;
}

/**
 * Event returned by GET /api/cases/:id/events. The User relation is
 * preloaded server-side via the FK association and rendered under
 * HAL's `_embedded` envelope (the goat convention for FK relations on
 * a resource). Every event has a user — user_id is required at the
 * DB and at the type level.
 */
export interface CaseEvent {
  id: string;
  project_id: string;
  user_id: string;
  /** The actor's firm role when the event was written; "System" on the
   *  server's own. */
  user_role: string;
  state: EventState;
  conditions: EventCondition[];
  /** Why a system task stopped or what it produced. Server-written only. */
  detail: string | null;
  created_at: string;
  _embedded: {
    user: User;
  };
}

/** Wrapper response shape for GET /api/events?case_id=:id. Mirrors
 *  the `{ events }` envelope the page used to consume so call sites
 *  stay unchanged after the move to the canonical list endpoint. */
export interface CaseEventsResponse {
  events: CaseEvent[];
}

export type EventCollection = HalCollection<CaseEvent>;

export const caseEventsQueryKey = (caseId: string) =>
  ["cases", caseId, "events"] as const;

const CASE_DETAIL_EVENTS_PAGE_SIZE = 500;

// Keyed by the URL case id (so realtime ["cases", caseId] invalidation
// reaches it) but fetched by the case's project id — events are
// project-scoped now (TMF-170).
export const caseEventsQueryOptions = (caseId: string, projectId: string) =>
  queryOptions<CaseEventsResponse>({
    queryKey: caseEventsQueryKey(caseId),
    queryFn: async ({ signal }) => {
      const collection = await halFetch<EventCollection>(
        `/api/events?project_id=${encodeURIComponent(projectId)}&size=${CASE_DETAIL_EVENTS_PAGE_SIZE}`,
        { signal },
      );
      return { events: collection._embedded ?? [] };
    },
  });

// ── Mutations ────────────────────────────────────────────────────────

/**
 * Change a task's state by posting an event. Both Mark done
 * (state=`complete`) and Reopen (state=`todo`) flow through this
 * single hook — call sites name the user-facing action, the engine
 * just sees the resulting event.
 *
 * Invalidates queue, the case row, the case's tasks projection, and
 * the case's events feed so the UI re-fetches everything that depends
 * on plan state. Doesn't optimistically update — the per-row optimism
 * lives in the route component since it has the fuller context (which
 * row, which list).
 */
/** One task state change posted alongside the primary one (same event). */
export interface TaskCondition {
  taskId: string;
  state: EventState;
}

/** The wire arguments of a task state change. */
export interface PostTaskStateEventArgs {
  /** Events are project-scoped; the workflow instance is addressed by its
   *  project whatever subtype (case, lead, document upload) fronts it. */
  projectId: string;
  /** The task row's id; the API stores the condition with that row's identity. */
  taskId: string;
  state: EventState;
  companions?: TaskCondition[] | undefined;
}

/**
 * THE task-state POST. Every subtype's mutation goes through this one
 * declaration of the request shape, so a lead event and a case event are
 * byte-identical on the wire; the hooks around it differ only in which
 * caches they invalidate afterwards.
 *
 * Body matches Go's models.EventRequest: { project_id, conditions[] }. The
 * primary task change plus any companions are posted together so the engine
 * applies them as one atomic event.
 */
export function postTaskStateEvent({
  projectId,
  taskId,
  state,
  companions,
}: PostTaskStateEventArgs): Promise<CaseEvent> {
  return halFetch<CaseEvent>("/api/events", {
    method: "POST",
    body: {
      project_id: projectId,
      conditions: [
        { task_id: taskId, state },
        ...(companions ?? []).map((c) => ({ task_id: c.taskId, state: c.state })),
      ],
    },
  });
}

export interface ChangeTaskStateArgs {
  // projectId scopes the POST (events are project-scoped now); caseId
  // keys the cache invalidation (the case-detail queries live under
  // ["cases", caseId, …]). Both are needed because the workflow instance
  // is addressed by case in the SPA but owned by the project in the API.
  projectId: string;
  caseId: string;
  taskId: string;
  state: EventState;
  // companions are extra task state changes applied in the SAME event — for
  // actions the SPA knows resolve several tasks at once, e.g. approving an
  // identity review also completes the "Verify client name" checklist task.
  companions?: TaskCondition[];
}

export function useChangeTaskState() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ projectId, taskId, state, companions }: ChangeTaskStateArgs) =>
      postTaskStateEvent({ projectId, taskId, state, companions }),
    onSuccess: (_event, variables) => {
      invalidateAfterTaskStateChange(queryClient, variables.caseId);
    },
  });
}

/**
 * Centralized invalidation so optimistic update sites can call the
 * same set without duplicating keys. Hits the case row and events
 * feed — both written synchronously inside POST /api/events, so the
 * refetch is safe to fire immediately.
 *
 * Tasks queries (TASKS_LIST_KEY, caseTasksQueryKey) are deliberately
 * NOT invalidated here: the lifecycle engine processes the event
 * asynchronously (queue → consumer), so an immediate refetch races
 * ahead of the consumer and returns stale state — clobbering the
 * route's optimistic update with a server-side "todo" right after
 * the user clicked Mark done. The optimistic value stays in the
 * cache until a natural refetch on navigation or window focus, by
 * which time the consumer has caught up. If/when the events endpoint
 * goes synchronous (handler runs inline rather than dispatching to
 * SNS), this can re-add the tasks invalidations safely.
 */
export function invalidateAfterTaskStateChange(
  queryClient: QueryClient,
  caseId: string,
) {
  queryClient.invalidateQueries({ queryKey: CASES_LIST_KEY });
  queryClient.invalidateQueries({ queryKey: caseEventsQueryKey(caseId) });
}

// ── History feed verb mapping ────────────────────────────────────────

/**
 * Map an event to a human-readable verb for the history feed. Some
 * verbs depend on the prior state of the same task — most importantly
 * "Reopened" only applies when `todo` follows a non-todo state.
 *
 * `priorState` is the most recent prior state for THIS task in the
 * case's event history, or `undefined` if this is the first event for
 * the task. The history reducer (`reduceHistoryVerbs`) walks events
 * chronologically and tracks per-task prior state for the caller.
 */
export function verbForEvent(
  state: EventState,
  priorState: EventState | undefined,
): string {
  switch (state) {
    case "complete":
      return "Completed";
    case "todo":
      // Reopened only when transitioning back from non-todo. The
      // initial todo (no prior state) reads as "Started" so the
      // history doesn't lie about a fresh task.
      if (priorState !== undefined && priorState !== "todo") return "Reopened";
      return "Started";
    case "in progress":
      return "Started";
    case "cancelled":
      return "Cancelled";
    case "rejected":
      return "Rejected";
    case "delayed":
      return "Delayed";
    case "un-cancel":
      return "Un-cancelled";
    case "error":
      return "Errored";
    case "":
    default:
      return "Updated";
  }
}

/**
 * Walk an event list (any order) and produce a parallel array of
 * verbs, computing each event's verb against the most recent prior
 * state of its target task. Returns verbs in the same order as the
 * input, regardless of the input's chronological direction.
 */
export function annotateEventsWithVerbs(
  events: CaseEvent[],
): { event: CaseEvent; verb: string }[] {
  const verbByEventId = new Map(
    priorStates(events, (e) => e.state).map(({ event, prior }) => [
      event.id,
      verbForEvent(event.state, prior),
    ]),
  );
  return events.map((e) => ({
    event: e,
    verb: verbByEventId.get(e.id) ?? "Updated",
  }));
}

/** The events oldest first, each with the state its task was last left in by
 *  an earlier event, undefined for the task's first. `stateOf` reads the state
 *  an event leaves its task in. */
function priorStates(
  events: CaseEvent[],
  stateOf: (event: CaseEvent) => EventState,
): { event: CaseEvent; prior: EventState | undefined }[] {
  const lastStateByTask = new Map<string, EventState>();
  return [...events]
    .sort((a, b) => new Date(a.created_at).getTime() - new Date(b.created_at).getTime())
    .map((event) => {
      const taskName = taskLabelFromCondition(event.conditions[0]?.task);
      const prior = lastStateByTask.get(taskName);
      lastStateByTask.set(taskName, stateOf(event));
      return { event, prior };
    });
}

// ── The push's history ───────────────────────────────────────────────

export interface HandoffHistoryRow {
  event: CaseEvent;
  outcome: string;
  reason: string | null;
  actor: string;
  tone: "danger" | "success" | "neutral";
}

const HANDOFF_HISTORY_TYPES: ReadonlySet<string> = new Set([
  HANDOFF_TASK_TYPE,
  RESOLVE_CONTACT_TASK_TYPE,
  RETRY_HANDOFF_TASK_TYPE,
]);

/** The push's attempts and the decisions between them, oldest first. An
 *  event's own `state` is its processing status, so the task's move is read
 *  off its condition. */
export function handoffHistory(events: CaseEvent[]): HandoffHistoryRow[] {
  const pushEvents = events.filter((e) => HANDOFF_HISTORY_TYPES.has(e.conditions[0]?.task?.type ?? ""));
  return priorStates(pushEvents, (e) => e.conditions[0]?.state ?? "").map(({ event, prior }) => {
    const condition = event.conditions[0];
    return {
      event,
      ...handoffOutcome(condition?.task?.type ?? "", condition?.state ?? "", prior, event.detail),
      actor: eventActor(event),
    };
  });
}

function handoffOutcome(
  type: string,
  state: EventState,
  prior: EventState | undefined,
  detail: string | null,
): Pick<HandoffHistoryRow, "outcome" | "reason" | "tone"> {
  const said = detail === null || detail.trim() === "" ? null : detail;
  if (type === HANDOFF_TASK_TYPE && (state === "rejected" || state === "error")) {
    return { outcome: "Stopped", reason: said ?? "no reason recorded", tone: "danger" };
  }
  if (type === HANDOFF_TASK_TYPE && state === "complete") {
    return { outcome: "Pushed", reason: said, tone: "success" };
  }
  if (type === RESOLVE_CONTACT_TASK_TYPE && state === "complete") {
    return { outcome: "Contact decided", reason: null, tone: "neutral" };
  }
  if (type === RETRY_HANDOFF_TASK_TYPE && state === "complete") {
    return { outcome: "Retried", reason: null, tone: "neutral" };
  }
  return { outcome: verbForEvent(state, prior), reason: null, tone: "neutral" };
}

function eventActor(event: CaseEvent): string {
  if (event.user_role === "System") return "System";
  const user = event._embedded.user;
  const name = `${user.first_name} ${user.last_name}`.trim();
  return name === "" ? "—" : name;
}
